<script>
	// NPM Modules
	import { fly } from 'svelte/transition';
	import debounce from 'lodash/debounce.js';

	export let id = '';
	export let placeholder = '';
	export let className = '';
	export let options = [];
	export let searchFunction = false;
	export let value = '';

	let input,
		activeOption,
		debounceSearchFunction,
		showOptions = false;

	$: filtered = options.filter((o) => (value ? o.toLowerCase().includes(value.toLowerCase()) : o));

	if (searchFunction) {
		debounceSearchFunction = debounce(searchFunction, 800);
		debounceSearchFunction();
	}

	function optionsVisibility(show) {
		if (typeof show === 'boolean') {
			showOptions = show;
			show && input.focus();
		} else {
			showOptions = !showOptions;
		}
		if (!showOptions) {
			activeOption = undefined;
		}
	}

	function handleKeyup(e) {
		showOptions = true;
		if (e.keyCode === 13) {
			// enter key
			value = activeOption;
			handleBlur();
		} else if (e.keyCode === 27) {
			// Esc key
			handleBlur();
		} else if ([38, 40].includes(e.keyCode)) {
			// up and down arrows
			const increment = e.keyCode === 38 ? -1 : 1;
			const calcIndex = filtered.indexOf(activeOption) + increment;
			activeOption =
				calcIndex < 0
					? filtered[filtered.length - 1]
					: calcIndex === filtered.length
						? filtered[0]
						: filtered[calcIndex];
		} else if (searchFunction) debounceSearchFunction(value);
	}

	function handleBlur() {
		optionsVisibility(false);
	}

	function handleOptionMousedown(e) {
		const newValue = e.target.dataset.value;
		if (value === newValue) {
			value = '';
		} else {
			value = newValue;
			input.focus();
		}
	}
</script>

<div class="relative py-1 form-input {className}">
	<div class="relative flex flex-wrap items-center" on:click={() => optionsVisibility()}>
		<div class="flex items-center flex-1">
			<input
				{id}
				class="w-full leading-normal focus:outline-none"
				autocomplete="off"
				bind:value
				bind:this={input}
				on:keyup={handleKeyup}
				on:blur={handleBlur}
				{placeholder}
			/>
			<button
				class="flex items-center justify-center ml-1 text-gray-500 bg-gray-300 rounded-lg cursor-pointer hover:bg-gray-400"
				title="Clear"
				class:hidden={!value}
				on:click={() => {
					value = '';
					if (searchFunction) debounceSearchFunction(value);
					input.focus();
				}}
			>
				<svg class="icon-clear" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
					<path
						d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19
            12 13.41 17.59 19 19 17.59 13.41 12z"
					/>
				</svg>
			</button>
			<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
				<path d="M5 8l4 4 4-4z" />
			</svg>
		</div>
	</div>

	{#if showOptions}
		<ul
			class="absolute left-0 w-full mt-1 overflow-auto list-none bg-white border border-gray-200 rounded-b-lg z-50"
			transition:fly={{ duration: 200, y: 5 }}
			on:mousedown|preventDefault={handleOptionMousedown}
		>
			{#each filtered as option}
				<li
					class:bg-gray-300={activeOption === option}
					class="py-2 pl-2 border-b border-gray-200 hover:bg-gray-100"
					data-value={option}
				>
					{option}
				</li>
			{/each}
		</ul>
	{/if}
</div>
